// Site run history — /websites/:id/runs
//
// Lists usage_events for the site (joined to article title where possible),
// shows model_key + resolved_via per row, filterable by model. Click-through
// to /articles/:id reveals the per-article audit chain (handled in the
// article detail page in a follow-up; here the link is a stub).
import { useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { db } from "@/lib/billing";

type Row = {
  id: string;
  task_type: string;
  model_key: string | null;
  resolved_via: string | null;
  credits_charged: number;
  status: string;
  created_at: string;
  article_id: string | null;
  latency_ms: number | null;
};

async function fetchRuns(websiteId: string): Promise<Row[]> {
  const { data, error } = await db
    .from("usage_events")
    .select("id, task_type, model_key, resolved_via, credits_charged, status, created_at, article_id, latency_ms")
    .eq("website_id", websiteId)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  return (data as Row[]) ?? [];
}

export default function SiteRunsPage() {
  const { id: websiteId } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [modelFilter, setModelFilter] = useState<string>("__all");

  const site = useQuery({
    queryKey: ["site-min", websiteId],
    enabled: !!websiteId,
    queryFn: async () => {
      const { data, error } = await db.from("websites").select("id, name").eq("id", websiteId).maybeSingle();
      if (error) throw error;
      return data as { id: string; name: string } | null;
    },
  });

  const runs = useQuery({
    queryKey: ["site-runs", websiteId],
    enabled: !!websiteId,
    queryFn: () => fetchRuns(websiteId!),
  });

  const modelKeys = useMemo(() => {
    const set = new Set<string>();
    for (const r of runs.data ?? []) if (r.model_key) set.add(r.model_key);
    return Array.from(set).sort();
  }, [runs.data]);

  const filtered = useMemo(() => {
    if (modelFilter === "__all") return runs.data ?? [];
    return (runs.data ?? []).filter((r) => r.model_key === modelFilter);
  }, [runs.data, modelFilter]);

  if (runs.isLoading || site.isLoading) {
    return (
      <div className="container mx-auto py-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-4 max-w-6xl">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to={`/websites/${websiteId}`}><ArrowLeft className="h-4 w-4 mr-1" /> {site.data?.name}</Link>
        </Button>
      </div>
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Run history</h1>
        <Select value={modelFilter} onValueChange={setModelFilter}>
          <SelectTrigger className="w-64"><SelectValue placeholder="All models" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All models</SelectItem>
            {modelKeys.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>When</TableHead>
                <TableHead>Task</TableHead>
                <TableHead>Model</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Credits</TableHead>
                <TableHead className="text-right">Latency</TableHead>
                <TableHead>Article</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
                  <TableCell><Badge variant="outline">{r.task_type}</Badge></TableCell>
                  <TableCell className="text-xs">{r.model_key ?? "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.resolved_via ?? "—"}</TableCell>
                  <TableCell>
                    <Badge variant={r.status === "success" ? "secondary" : r.status === "refunded" ? "outline" : "destructive"}>
                      {r.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">{r.credits_charged}</TableCell>
                  <TableCell className="text-right text-xs">{r.latency_ms ?? "—"} ms</TableCell>
                  <TableCell>
                    {r.article_id ? (
                      <Link to={`/articles/${r.article_id}`} className="text-xs underline hover:text-foreground">
                        view
                      </Link>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-12">No runs yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
